import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLanguageDisplayName } from '../config/languages';
import { useLearningContext } from '../hooks/useLearningContext';
import { learningProgramSystemLabel, learningProgramTypeLabel } from '../utils/learningProgramDisplay';
import './LearningContextSwitcher.css';

const asArray = (value) => (Array.isArray(value) ? value : []);

function programIcon(programType) {
  if (programType === 'public_cohort') return '\u{1F465}';
  if (programType === 'institution_cohort') return '\u{1F3EB}';
  if (programType === 'institution_self_paced') return '\u{1F4DA}';
  if (programType === 'personal_semester') return '\u{1F4C5}';
  return '\u{1F464}';
}

function programLabel(program, t) {
  if (program?.programType === 'institution_self_paced' && program.organizationName) {
    return t('learningProgram.organizationLearning', {
      organization: program.organizationName,
      defaultValue: '{{organization}} learning',
    });
  }
  return program?.nameKey
    ? learningProgramSystemLabel(program, t)
    : t('learningProgram.personalName', 'Personal learning');
}

function programDetail(program, t) {
  const parts = [];
  if (program?.name) parts.push(program.name);
  parts.push(learningProgramTypeLabel(program?.programType, t));
  if (program?.targetLanguage) {
    parts.push(getLanguageDisplayName(program.targetLanguage, t));
  }
  if (program?.level) {
    parts.push(t('learningProgram.level', {
      level: program.level,
      defaultValue: 'Level {{level}}',
    }));
  }
  return parts.filter(Boolean).join(' · ');
}

function sponsorGrantsFor(program) {
  return asArray(program?.access?.grants).filter((grant) => grant?.organizationName);
}

function sponsorCountFor(program) {
  const reportedCount = Number(program?.access?.sponsoredByCount);
  return Number.isFinite(reportedCount) && reportedCount > 0
    ? reportedCount
    : sponsorGrantsFor(program).length;
}

function grantStatusLabel(status, t) {
  switch (String(status || '').toLowerCase()) {
    case 'active': return t('learningProgram.grantStatus.active', 'Active');
    case 'trialing': return t('learningProgram.grantStatus.trialing', 'Trial');
    case 'expired': return t('learningProgram.grantStatus.expired', 'Expired');
    case 'pending': return t('learningProgram.grantStatus.pending', 'Pending');
    case 'inactive':
    case 'none': return t('learningProgram.grantStatus.inactive', 'Inactive');
    default: return t('learningProgram.grantStatus.provided', 'Access provided');
  }
}

function formatGrantDate(value, locale) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale || undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function groupFor(program, t) {
  if (program.programType === 'personal_self_paced' || program.programType === 'personal_semester') {
    return { key: 'personal', label: t('learningProgram.groups.mine', 'My learning') };
  }
  if (program.programType === 'public_cohort') {
    return { key: 'lingo-booth', label: t('learningProgram.groups.lingoBooth', 'Lingo Booth') };
  }
  return {
    key: `organization:${program.organizationId || program.organizationName || 'other'}`,
    label: program.organizationName || t('learningProgram.groups.organizations', 'Organizations'),
  };
}

// Current Program is deliberately separate from billing. A program controls
// curriculum, progress, and community; its nested grants only explain who is
// providing access. The program API is preferred, while the hook supplies a
// backward-compatible view of legacy Personal/Institution contexts.
function LearningContextSwitcher() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    programs,
    activeProgram,
    activeProgramId,
    loading,
    switchProgram,
  } = useLearningContext();
  const [open, setOpen] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const [switchError, setSwitchError] = useState('');
  const [switchNotice, setSwitchNotice] = useState('');
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab' || !menuRef.current) return;
      const focusable = [...menuRef.current.querySelectorAll('button:not([disabled]), a[href]')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const focusTimer = window.requestAnimationFrame(() => {
      menuRef.current?.querySelector('button:not([disabled]), a[href]')?.focus();
    });
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.cancelAnimationFrame(focusTimer);
    };
  }, [open]);

  useEffect(() => { setOpen(false); }, [activeProgramId]);

  useEffect(() => {
    if (!switchNotice) return undefined;
    const timer = window.setTimeout(() => setSwitchNotice(''), 3500);
    return () => window.clearTimeout(timer);
  }, [switchNotice]);

  const groupedPrograms = useMemo(() => {
    const groups = new Map();
    asArray(programs).forEach((program) => {
      const group = groupFor(program, t);
      if (!groups.has(group.key)) groups.set(group.key, { ...group, programs: [] });
      groups.get(group.key).programs.push(program);
    });
    return [...groups.values()];
  }, [programs, t]);

  // A selector with one valid option adds noise without giving the learner a
  // choice. Inaccessible commitments do not make this control useful.
  const availablePrograms = asArray(programs).filter((program) => program?.access?.available !== false);
  if (!activeProgram || availablePrograms.length <= 1) return null;

  const activeSponsorCount = sponsorCountFor(activeProgram);
  const canOpen = true;

  const onSelect = async (program) => {
    if (program.programId === activeProgramId || busyKey) return;
    const mayHaveUnsavedWork = (
      location.pathname === '/writing'
      || location.pathname === '/conversation'
      || /^\/quiz\/.+/.test(location.pathname)
      || /^\/class\/.+/.test(location.pathname)
      || (location.pathname.startsWith('/learn/v2') && location.pathname !== '/learn/v2/catalog')
    );
    if (mayHaveUnsavedWork && !window.confirm(t('learningProgram.leaveActivityConfirm', 'Switch programs and leave this activity? Unsaved work may be lost.'))) {
      return;
    }
    setBusyKey(program.programId);
    setSwitchError('');
    try {
      await switchProgram(program);
      setSwitchNotice(t('learningProgram.switchedTo', {
        program: programLabel(program, t),
        defaultValue: 'Switched to {{program}}',
      }));
      // A switch always lands on Learn. Staying where the learner was let a
      // cohort semester (or any other program's screen) keep running under the
      // program they had just left; Learn is rebuilt for the new selection.
      navigate('/', { replace: true });
      window.scrollTo(0, 0);
    } catch (_) {
      setSwitchError(t('learningProgram.switchFailed', 'Could not switch programs. Please try again.'));
    } finally {
      setBusyKey('');
    }
  };

  const closeMenu = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className={`lc-switcher${open ? ' open' : ''}`} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="lc-switcher-trigger"
        aria-busy={loading}
        aria-haspopup={canOpen ? 'dialog' : undefined}
        aria-expanded={canOpen ? open : undefined}
        aria-controls={canOpen ? 'learning-program-menu' : undefined}
        aria-label={canOpen
          ? t('learningProgram.menuAria', 'Choose current program')
          : t('learningProgram.currentAria', 'Current learning program')}
        onClick={() => canOpen && setOpen((value) => !value)}
        disabled={!canOpen || loading}
      >
        <span className="lc-switcher-icon" aria-hidden="true">{programIcon(activeProgram.programType)}</span>
        <span className="lc-switcher-text">
          <span className="lc-switcher-kicker">{t('learningProgram.currentLabel', 'Current program')}</span>
          <span className="lc-switcher-label">{programLabel(activeProgram, t)}</span>
          <span className="lc-switcher-detail">{programDetail(activeProgram, t)}</span>
        </span>
        {activeSponsorCount > 0 && (
          <span className="lc-switcher-sponsor-count" aria-label={t('learningProgram.sponsorCount', {
            count: activeSponsorCount,
            defaultValue: '{{count}} sponsors',
          })}>
            +{activeSponsorCount}
          </span>
        )}
        {canOpen && <span className="lc-switcher-caret" aria-hidden="true">{'▾'}</span>}
      </button>

      {switchNotice && <p className="lc-switcher-notice" role="status">{switchNotice}</p>}

      {open && (
        <>
          <button
            type="button"
            className="lc-switcher-backdrop"
            aria-label={t('common.close', 'Close')}
            onClick={closeMenu}
          />
          <div
            ref={menuRef}
            id="learning-program-menu"
            className="lc-switcher-menu"
            role="dialog"
            aria-modal="true"
            aria-labelledby="learning-program-menu-title"
          >
            <div className="lc-switcher-menu-head">
              <div>
                <span>{t('learningProgram.currentLabel', 'Current program')}</span>
                <h2 id="learning-program-menu-title">{t('learningProgram.switchTitle', 'Choose a learning program')}</h2>
              </div>
              <button type="button" onClick={closeMenu} aria-label={t('common.close', 'Close')}>×</button>
            </div>
            <p className="lc-switcher-menu-hint">
              {t('learningProgram.switchHint', 'Lessons, progress, and scores will change with your program.')}
            </p>

            <div className="lc-switcher-groups" role="listbox" aria-label={t('learningProgram.programListAria', 'Available learning programs')}>
              {groupedPrograms.map((group) => (
                <section className="lc-switcher-group" key={group.key} role="group" aria-label={group.label}>
                  <h3>{group.label}</h3>
                  {group.programs.map((program) => {
                    const active = program.programId === activeProgramId;
                    const grants = sponsorGrantsFor(program);
                    const sponsorCount = sponsorCountFor(program);
                    return (
                      <div className={`lc-switcher-option-wrap${active ? ' active' : ''}`} key={program.programId}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={active}
                          className="lc-switcher-option"
                          onClick={() => onSelect(program)}
                          disabled={!!busyKey || active || program.access?.available === false}
                        >
                          <span className="lc-switcher-option-icon" aria-hidden="true">{programIcon(program.programType)}</span>
                          <span className="lc-switcher-option-text">
                            <span className="lc-switcher-option-label">{programLabel(program, t)}</span>
                            <span className="lc-switcher-option-detail">{programDetail(program, t)}</span>
                            {program.access?.available === false && (
                              <span className="lc-switcher-option-unavailable">{t('learningProgram.accessUnavailable', 'Access unavailable')}</span>
                            )}
                          </span>
                          {active && <span className="lc-switcher-check" aria-label={t('learningProgram.current', 'Current')}>{'✓'}</span>}
                          {busyKey === program.programId && <span className="lc-switcher-spinner" aria-label={t('common.loading', 'Loading...')} />}
                        </button>

                        {sponsorCount > 0 && (
                          <div className="lc-program-access">
                            <span className="lc-program-access-title">
                              {sponsorCount === 1
                                ? t('learningProgram.accessProvidedBy', 'Access provided by')
                                : t('learningProgram.sponsoredByOrganizations', {
                                  count: sponsorCount,
                                  defaultValue: 'Sponsored by {{count}} organizations',
                                })}
                            </span>
                            {grants.length > 0 && <ul>
                              {grants.map((grant) => {
                                const expiry = formatGrantDate(grant.expiresAt, i18n.resolvedLanguage || i18n.language);
                                return (
                                  <li key={grant.grantId}>
                                    <span aria-hidden="true">{'🏢'}</span>
                                    <span>
                                      <strong>{grant.organizationName || t('learningProgram.sponsorFallback', 'Sponsoring organization')}</strong>
                                      <small>
                                        {grantStatusLabel(grant.status, t)}
                                        {expiry ? ` · ${t('learningProgram.expiresOn', { date: expiry, defaultValue: 'Ends {{date}}' })}` : ''}
                                      </small>
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </section>
              ))}
            </div>
            {switchError && <p className="lc-switcher-error" role="alert">{switchError}</p>}
          </div>
        </>
      )}
    </div>
  );
}

export default LearningContextSwitcher;
